import type { Product } from '@/pages/cardapio/types';

interface ProductDetailPanelProps {
  product: Product;
  quantity: number;
  onQuantity: (qty: number) => void;
  onAddToCart: () => void;
  onClose: () => void;
}

const ProductDetailPanel = ({
  product,
  quantity,
  onQuantity,
  onAddToCart,
  onClose,
}: ProductDetailPanelProps) => {
  return (
    <div className="relative mt-12 overflow-hidden rounded-3xl border border-secondary-500/20 bg-background-100">
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Fechar detalhes"
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-background-300/70 bg-background-50/70 text-foreground-400 backdrop-blur-sm transition-colors hover:border-secondary-500/50 hover:text-secondary-400 cursor-pointer"
      >
        <i className="ri-close-line text-lg" />
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-background-200 lg:aspect-auto">
          <img
            src={product.image}
            alt={product.name}
            title={`${product.name} — detalhes do sanduíche Naturalle`}
            className="h-full w-full object-cover object-top"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background-100/70 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="flex flex-col p-6 md:p-10">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="font-label text-xs font-medium uppercase tracking-wider text-secondary-500">
              {product.category}
            </span>
            <span className="rounded-full border border-secondary-500/25 px-2.5 py-0.5 font-label text-xs font-medium text-foreground-500">
              {product.weight}
            </span>
          </div>

          <h2 className="mb-3 font-display text-2xl text-foreground-950 md:text-3xl">
            {product.name}
          </h2>
          <p className="mb-6 text-sm leading-relaxed text-foreground-500">{product.description}</p>

          {/* Tags */}
          <div className="mb-6 flex flex-wrap gap-2">
            {product.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-accent-500/25 bg-accent-500/10 px-3 py-1.5 font-label text-xs font-semibold text-accent-400"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Info blocks */}
          <div className="mb-6 flex-1 space-y-5">
            <div>
              <h4 className="mb-1.5 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
                <i className="ri-list-check text-secondary-500" /> Ingredientes
              </h4>
              <p className="text-sm leading-relaxed text-foreground-500">{product.ingredients}</p>
            </div>
            <div>
              <h4 className="mb-1.5 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
                <i className="ri-alert-line text-accent-400" /> Alergênicos
              </h4>
              <p className="text-sm leading-relaxed text-foreground-500">{product.allergens}</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <h4 className="mb-1.5 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
                  <i className="ri-temp-cold-line text-secondary-500" /> Conservação
                </h4>
                <p className="text-xs leading-relaxed text-foreground-500">{product.storage}</p>
              </div>
              <div>
                <h4 className="mb-1.5 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
                  <i className="ri-calendar-check-line text-accent-400" /> Validade
                </h4>
                <p className="text-xs leading-relaxed text-foreground-500">{product.validity}</p>
              </div>
            </div>
            <div>
              <h4 className="mb-1.5 flex items-center gap-2 font-heading text-sm font-semibold text-foreground-900">
                <i className="ri-archive-line text-secondary-500" /> Embalagem
              </h4>
              <p className="text-xs leading-relaxed text-foreground-500">{product.packaging}</p>
            </div>
          </div>

          {/* Nutritional table */}
          <div className="mb-6">
            <h4 className="mb-3 font-heading text-sm font-semibold text-foreground-900">
              Informação Nutricional
            </h4>
            <div className="mb-2 text-xs text-foreground-500">
              Porção: {product.nutritional.portion} | {product.nutritional.portionsPerPack}
            </div>
            <div className="overflow-x-auto rounded-xl border border-background-200/70">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-200/40">
                    <th className="py-2.5 pl-3 text-left font-label font-medium text-foreground-600"></th>
                    <th className="py-2.5 text-right font-label font-medium text-foreground-600">100g</th>
                    <th className="py-2.5 text-right font-label font-medium text-foreground-600">Porção</th>
                    <th className="py-2.5 pr-3 text-right font-label font-medium text-foreground-600">%VD</th>
                  </tr>
                </thead>
                <tbody>
                  {product.nutritional.items.map((item, i) => (
                    <tr key={i} className="border-b border-background-200/50 last:border-b-0">
                      <td className="py-2 pl-3 text-foreground-500">{item.label}</td>
                      <td className="py-2 text-right text-foreground-500">{item.per100g}</td>
                      <td className="py-2 text-right text-foreground-500">{item.perPortion}</td>
                      <td className="py-2 pr-3 text-right text-foreground-400">{item.vd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[0.65rem] text-foreground-400">
              *Percentual de valores diários fornecidos pela porção.
            </p>
          </div>

          {/* Quantity + Add to cart */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <button
                onClick={() => onQuantity(quantity - 1)}
                aria-label="Diminuir quantidade"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/70 text-foreground-400 transition-colors hover:border-secondary-500/50 hover:text-secondary-400 cursor-pointer"
              >
                <i className="ri-subtract-line text-sm" />
              </button>
              <span className="w-10 text-center font-label font-semibold text-foreground-900">
                {quantity}
              </span>
              <button
                onClick={() => onQuantity(quantity + 1)}
                aria-label="Aumentar quantidade"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/70 text-foreground-400 transition-colors hover:border-secondary-500/50 hover:text-secondary-400 cursor-pointer"
              >
                <i className="ri-add-line text-sm" />
              </button>
            </div>
            <button
              onClick={onAddToCart}
              className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 font-label text-sm font-bold text-white transition-colors hover:bg-accent-400 cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line"></i>
              Adicionar ao Carrinho
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPanel;